/**
 * User service — handles user profile creation and retrieval
 */
import { doc, setDoc, getDoc, getDocs, updateDoc, collection, query, where, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { sanitizeString, sanitizeEmail } from '../utils/sanitize';
import { createLogger } from '../utils/logger';

const log = createLogger('userService');

/**
 * Create a user profile document in Firestore
 * @param {Object} params
 * @param {string} params.uid - Firebase Auth UID
 * @param {string} params.email - User email
 * @param {string} params.displayName - User display name
 * @param {string} [params.role='client'] - User role
 * @param {string} [params.trainerId] - Trainer who invited this client
 * @returns {Promise<void>}
 */
export const createUserProfile = async ({ uid, email, displayName, role = 'client', trainerId, isBeta = false }) => {
  const userData = {
    email: sanitizeEmail(email),
    displayName: sanitizeString(displayName, 100),
    role,
    isBeta: isBeta || false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (trainerId) userData.trainerId = trainerId;

  await setDoc(doc(db, 'users', uid), userData);
  log.info('User profile created', { uid, role });
};

/**
 * Get a user profile by UID
 * @param {string} uid
 * @returns {Promise<Object|null>}
 */
export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
};

/**
 * Get all clients (users with role 'client')
 * @returns {Promise<Array>}
 */
export const getClients = async () => {
  const q = query(collection(db, 'users'), where('role', '==', 'client'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
};

/**
 * Get all clients linked to a trainer (via invite)
 * @param {string} trainerId
 * @returns {Promise<Array>}
 */
export const getTrainerClients = async (trainerId) => {
  const q = query(
    collection(db, 'users'),
    where('role', '==', 'client'),
    where('trainerId', '==', trainerId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
};

/**
 * Upload a user's profile photo
 * @param {string} uid - User's UID
 * @param {File|Blob} file - Image file
 * @returns {Promise<string>} Download URL
 */
export const uploadUserPhoto = async (uid, file) => {
  const storageRef = ref(storage, `users/${uid}/photo`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  await updateDoc(doc(db, 'users', uid), {
    photoUrl: url,
    updatedAt: serverTimestamp(),
  });
  log.info('User photo uploaded', { uid });
  return url;
};

/**
 * Delete a user's profile photo
 * @param {string} uid - User's UID
 * @returns {Promise<void>}
 */
export const deleteUserPhoto = async (uid) => {
  const storageRef = ref(storage, `users/${uid}/photo`);
  try {
    await deleteObject(storageRef);
  } catch (err) {
    if (err.code !== 'storage/object-not-found') throw err;
  }
  await updateDoc(doc(db, 'users', uid), {
    photoUrl: null,
    updatedAt: serverTimestamp(),
  });
  log.info('User photo deleted', { uid });
};
